import { first, get, sortBy, uniqBy } from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';
import { TwilioAreaCodes, TwilioNumber } from '../interfaces/twilio';
import { FormattedBot } from '../interfaces/view';
import ChatbotModel from '../model/ChatbotModel';
import CompanyModel from '../model/CompanyModel';
import CompanyUsersModel from '../model/CompanyUsersModel';
import ConnectedPageModel from '../model/ConnectedPageModel';
import PageModel from '../model/PageModel';
import { TemplateModel } from '../model/TemplateModel';
import { StoreState } from '../store-interfaces';
import { selectIsShopifyUser } from './userSelector';
import {
  activeFacebookPageSelector,
  selectActiveFacebookPage,
} from './facebookPageSelector';

export const selectCompanies = (state: StoreState) => state.company.entities;
export const selectActiveCompanyId = (state: StoreState) =>
  (state.session.session &&
    state.session.session.userAttributes &&
    state.session.session.userAttributes.activeCompanyId) ||
  -1;

export const selectActiveUserId = (state: StoreState) =>
  get(state, 'session.session.id') as number;

const selectBots = (state: StoreState) => state.chatbot.entities || [];

export const selectActiveChatbot = (state: StoreState) =>
  state.chatbot.activeChatbot;

export const selectSuppressEmbeddedChatWidget = (state: StoreState) =>
  state.view.suppressEmbeddedChatWidget;

const selectActiveChatbotId = (state: StoreState) =>
  state.view.chatbotEditor.activeChatbotId;
export const selectActiveChatbotBuildPageId = (state: StoreState) =>
  state.view.chatbotEditor.build.activePageId;
const selectPageEntities = (state: StoreState) => state.page.entities;
const getActiveChatbotEditorPage = (pageId: number, pages: PageModel[]) =>
  pages.find(p => p.id === pageId);

export const activeCompanyUsers = (state: StoreState) =>
  get(state, 'companyUsers.entities', []) as CompanyUsersModel[];
export const selectCurrentUser = (state: StoreState) =>
  get(state, 'companyUsers.currentUser', []) as CompanyUsersModel;
export const selectSampleBots = (state: StoreState): ChatbotModel[] =>
  get(state, 'sampleBots.entities', []);
export const selectTemplates = (state: StoreState): TemplateModel[] =>
  get(state, 'templates.entities', []);

export const getCompany = (
  companies: CompanyModel[],
  activeCompanyId: number,
): CompanyModel | undefined => {
  const activeCompany =
    companies && companies.find(c => c.id === activeCompanyId);

  return activeCompany || companies[0];
};

export const isRoleForActiveCompanyFBCollaborator = (
  activeUserId: number,
  activeCompanyUsers: CompanyUsersModel[],
) =>
  activeCompanyUsers.some(
    (user: CompanyUsersModel) =>
      user.id === activeUserId && user.role === 'Facebook page collaborator',
  );

export const getCurrentUserRole = (
  activeUserId: number,
  activeCompanyUsers: CompanyUsersModel[],
  isRoleCode?: boolean,
) => {
  const user = activeCompanyUsers.find(
    (user: CompanyUsersModel) => user.id === activeUserId,
  );
  return isRoleCode ? user?.roleCode : user?.role;
};

export const sortedAbcByName = (
  sampleBots: (ChatbotModel | TemplateModel)[],
): (ChatbotModel | TemplateModel)[] =>
  sortBy(sampleBots, (item: ChatbotModel | TemplateModel) => item.name);

export const sortedBotsSelector = createSelector(
  [selectSampleBots],
  sortedAbcByName,
);

export const sortedTemplatesSelector = createSelector(
  [selectTemplates],
  sortedAbcByName,
);

export const activeCompanySelector = createSelector(
  [selectCompanies, selectActiveCompanyId],
  getCompany,
);

export const isRoleForActiveCompanyFBCollaboratorSelector = createSelector(
  [selectActiveUserId, activeCompanyUsers],
  isRoleForActiveCompanyFBCollaborator,
);

export const currentUserRoleSelector = createSelector(
  [selectCurrentUser],
  user => user.role,
);

export const currentUserRoleCodeSelector = createSelector(
  [selectCurrentUser],
  user => user.roleCode,
);

const getActiveFacebookPageId = (activeChatbot?: ChatbotModel) => {
  if (
    activeChatbot &&
    activeChatbot.connectedPages &&
    activeChatbot.connectedPages[0]
  ) {
    return activeChatbot.connectedPages[0].id;
  }

  return null;
};

export const activeFacebookPageIdSelector = createSelector(
  [selectActiveChatbot],
  getActiveFacebookPageId,
);

const getFbPageId = (chatbot: ChatbotModel) => {
  const connectedPage = chatbot && chatbot.connectedPages[0];
  if (connectedPage) return connectedPage.remoteId;
};

const getConnectedPage = (
  bot?: ChatbotModel,
): ConnectedPageModel | undefined => {
  if (bot && bot.connectedPages) {
    return bot.connectedPages[0];
  }
};

const getIsCompanyAdmin = (activeCompany: CompanyModel) =>
  activeCompany ? activeCompany.editable : false;
const getActiveChatbot = (chatbot: ChatbotModel) =>
  chatbot || ({} as Partial<ChatbotModel>);

const getChatbotIsActiveOnConnectedPage = (
  chatbot?: ChatbotModel,
  connectedPage?: ConnectedPageModel,
) => chatbot && connectedPage && chatbot.id === connectedPage.activeBotId;

const getFormattedBots = (bots: ChatbotModel[]): FormattedBot[] => {
  const uniqueBots = uniqBy(bots, 'id');

  return uniqueBots
    .filter(bot => !bot.template)
    .map(bot => {
      const formattedCreatedAt = moment(bot.createdAt).format('MMMM Do, YYYY');
      const connectedPage = bot.connectedPages[0];
      const isActive = connectedPage
        ? bot.id === connectedPage.activeBotId
        : false;

      return {
        ...bot,
        formattedCreatedAt,
        isActive,
      };
    });
};

export const getFbPageIdSelector = createSelector(
  [selectActiveChatbot],
  getFbPageId,
);

export const activeChatbotSelector = createSelector(
  [selectActiveChatbot],
  getActiveChatbot,
);

export const connectedPageSelector = createSelector(
  [selectActiveChatbot],
  getConnectedPage,
);

export const companyAdminSelector = createSelector(
  [activeCompanySelector],
  getIsCompanyAdmin,
);

export const chatbotIsActiveOnConnectedPageSelector = createSelector(
  [selectActiveChatbot, connectedPageSelector],
  getChatbotIsActiveOnConnectedPage,
);

export const activeChatbotEditorPageSelector = createSelector(
  [selectActiveChatbotBuildPageId, selectPageEntities],
  getActiveChatbotEditorPage,
);

export const formattedBotsSelector = createSelector(
  [selectBots],
  getFormattedBots,
);

const getActiveChatbotId = (activeChatbotId: number) => activeChatbotId;

// Make it easier to remove this extra source of truth later
export const activeChatbotIdSelector = createSelector(
  [selectActiveChatbotId],
  getActiveChatbotId,
);

export const activeChatBotAnySetupStepsRemaining = createSelector(
  [selectActiveChatbot],
  bot => {
    if (!bot || !bot.setupStepsRemaining) {
      return false;
    }
    const { setupStepsRemaining } = bot;
    return Object.keys(setupStepsRemaining).some(
      (stepName: keyof typeof setupStepsRemaining) =>
        setupStepsRemaining[stepName] === 'pending',
    );
  },
);

export const activeChatBotAllSetupStepsRemaining = createSelector(
  [selectActiveChatbot],
  bot => {
    if (!bot || !bot.setupStepsRemaining) {
      return false;
    }
    const { setupStepsRemaining } = bot;
    return Object.keys(setupStepsRemaining).every(
      (stepName: keyof typeof setupStepsRemaining) =>
        setupStepsRemaining[stepName] === 'pending',
    );
  },
);

export const activeChatBotAllSetupStepsDismissed = createSelector(
  [selectActiveChatbot],
  bot => {
    if (!bot || !bot.setupStepsRemaining) {
      return true;
    }
    const { setupStepsRemaining } = bot;
    return Object.keys(setupStepsRemaining).every(
      (stepName: keyof typeof setupStepsRemaining) =>
        setupStepsRemaining[stepName] === 'dismissed',
    );
  },
);

export const areaCodesSelector = (state: StoreState) =>
  get(state, 'twilio.countryAreaCodes') as [
    TwilioAreaCodes[],
    TwilioAreaCodes[],
  ];

export const twilioNumbersSelector = (state: StoreState) =>
  get(state, 'twilio.numbers') as TwilioNumber[];

export const twilioNumbersToSelectSelector = (state: StoreState) =>
  get(state, 'twilio.numbersToSelect') as TwilioNumber[];

export const getTwilioAvailable = (state: StoreState) =>
  get(state, 'connectedPage.pageDetails.twilioAccessible') as boolean;

export const mainMenuSelector = (state: StoreState) =>
  state.view.chatbotEditor.mainMenuSelected;

export const selectWidgetIdToFocus = (state: StoreState) =>
  state.view.widgetToFocus;

export const selectWidgetLoaderOrder = (state: StoreState) =>
  state.view.widgetLoaderOrder;

export const selectIsShopifyContext = (state: StoreState) =>
  !!(state.view.appContext === 'shopify' || selectIsShopifyUser(state));

export const selectShopifyShops = (state: StoreState) =>
  state?.session?.session?.shops || [];

export const selectShopifyShop = (state: StoreState) =>
  first(state?.session?.session?.shops || []);

export const selectIsShopifyHasAnyPurchase = createSelector(
  [selectIsShopifyContext, selectShopifyShop, activeFacebookPageSelector],
  (isShopify, shop, fbPage) => {
    const subs = fbPage?.subscriptions;
    return (
      isShopify && shop && subs?.length && subs.some(s => s.state !== 'pending')
    );
  },
);

export const selectIsFreeShopify = createSelector(
  [selectActiveFacebookPage],
  page =>
    !page?.subscriptions?.length ||
    page.subscriptions?.every(sub =>
      ['canceled', 'expired', 'pending'].includes(sub.state),
    ),
);
